import { inject, computed, type ComputedRef } from "vue";
import { FEATURE_FLAG_SERVICE_KEY } from "../utils/symbols";

/**
 * Composable to check if a specific feature flag is enabled.
 *
 * @param {string} flagName - The name of the feature flag to check
 * @returns {ComputedRef<boolean>} Computed ref that returns true if the feature flag is enabled, false otherwise.
 * Returns false if the flag doesn't exist.
 *
 * @throws {Error} If used outside of FeatureFlagProvider
 *
 * @example
 * // Basic usage
 * <script setup>
 * import { useFeatureFlag } from '@flagpole/client-vue';
 *
 * const isNewFeatureEnabled = useFeatureFlag('new-feature');
 * </script>
 *
 * <template>
 *   <div v-if="isNewFeatureEnabled">
 *     <NewFeature />
 *   </div>
 * </template>
 *
 * @example
 * // With conditional logic
 * <script setup>
 * import { useFeatureFlag } from '@flagpole/client-vue';
 *
 * const isPremiumEnabled = useFeatureFlag('premium-feature');
 * const isBetaEnabled = useFeatureFlag('beta-feature');
 *
 * const showAdvancedUI = computed(() =>
 *   isPremiumEnabled.value && isBetaEnabled.value
 * );
 * </script>
 */
export function useFeatureFlag(flagName: string): ComputedRef<boolean> {
  const service = inject(FEATURE_FLAG_SERVICE_KEY);

  if (!service) {
    throw new Error("useFeatureFlag must be used within a FeatureFlagProvider");
  }

  return computed(() => service.isFeatureEnabled(flagName));
}
